import { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FlexColumn,
  FormControls,
  ChipList,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddTestForm, ColumnResource } from 'shared/types';
import SeveritySelector from '../SeveritySelector';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  columnsResourcesData: ColumnResource[];
};

const ColumnsAcceptedValues = ({ methods, columnsResourcesData }: Props) => {
  const { register, control, watch, setValue } = methods;
  const [expanded, setExpanded] = useState(true);

  const values = watch('values');
  const resources = watch('resources');

  const target = useMemo(() => {
    if (resources.length > 1) {
      return null;
    }
    const resource = resources[0];
    return columnsResourcesData.find((col) => col.id === resource);
  }, [columnsResourcesData, resources]);

  const descriptionText = useMemo(
    () =>
      target
        ? `The ${target.name} column in the ${target.model_name} table should be one of the following values:`
        : 'The column should be one of the following values:',
    [target],
  );

  useEffect(() => {
    setValue('description', `${descriptionText} ${values?.join(', ') || ''}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <FormControls.TestAccordion
      summary="Accepted values"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: 500 }}>Test configuration</Typography>
          <Controller
            name="severity"
            control={control}
            defaultValue={{
              conditionalError: { operator: '!=', value: 0 },
            }}
            render={({ field }) => (
              <SeveritySelector {...field} allowConditional />
            )}
          />
          <FormControls.Row>
            {target ? (
              <FormControls.FormText>
                The <b>{target.name}</b> column in the{' '}
                <b>{target.model_name}</b> table should be one of the following
                values:
              </FormControls.FormText>
            ) : (
              <FormControls.FormText>
                The column should be one of the following values:
              </FormControls.FormText>
            )}
            <Controller
              name="values"
              control={control}
              render={({ field }) => (
                <ChipList
                  {...field}
                  buttonText="Add value"
                  placeholder="Value"
                />
              )}
            />
          </FormControls.Row>
          <FormControls.Row>
            <Checkbox $noPadding {...register('valuesAreNotStrings')} />
            <FormControls.FormText>
              The provided values are not strings
            </FormControls.FormText>
            <InfoTooltip title="The provided values are not strings" />
          </FormControls.Row>
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <TableTestDescriptionField
          methods={methods}
          defaultValue={descriptionText}
        />

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default ColumnsAcceptedValues;
